/**
 * Supabase Sync Engine — Background sync van localStorage naar Supabase.
 *
 * ARCHITECTUUR:
 * - localStorage blijft de primaire, snelle datastore (offline-first)
 * - Na elke localStorage write wordt data async naar Supabase gepusht
 * - Fire-and-forget: sync failures worden gelogd maar blokkeren nooit de UI
 * - Debounced: meerdere snelle writes worden gebundeld
 *
 * FASE 1: Sync van D2 (MultiAccountState) — accounts, classrooms, relationships
 * FASE 2: Sync van D3-D11 (assessment, preferences, etc.)
 *
 * @see docs/project/SUPABASE_MIGRATION_PLAN.md
 */

import {
  getSupabaseClient,
  isSupabaseConfigured,
  isOnline,
} from './supabaseClient'
import type {
  DbAccount,
  DbClassroom,
  DbClassroomMember,
  DbRelationship,
  DbLearnerState,
} from './supabaseClient'
import { debug, warn, error as logError } from '../utils/logger'
import type { MultiAccountStateV4 } from '../accounts/logic/migration'
import type { Account, Classroom, RelationshipGraph } from '../accounts/types'

// ─── Debounce Configuration ───────────────────────────────────────────

const SYNC_DEBOUNCE_MS = 2000 // Wait 2s after last write before syncing
let syncTimer: ReturnType<typeof setTimeout> | null = null
let pendingState: MultiAccountStateV4 | null = null
let isSyncing = false

// ─── Sync Status (observable by UI if needed) ─────────────────────────

export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error' | 'offline'

let currentStatus: SyncStatus = 'idle'
let lastSyncAt: number | null = null
let lastError: string | null = null
const statusListeners: Array<(status: SyncStatus) => void> = []

function setStatus(status: SyncStatus, error?: string) {
  currentStatus = status
  lastError = error ?? null
  if (status === 'success') lastSyncAt = Date.now()
  statusListeners.forEach((fn) => fn(status))
}

/** Subscribe to sync status changes. Returns unsubscribe function. */
export function onSyncStatusChange(
  listener: (status: SyncStatus) => void,
): () => void {
  statusListeners.push(listener)
  return () => {
    const idx = statusListeners.indexOf(listener)
    if (idx >= 0) statusListeners.splice(idx, 1)
  }
}

/** Get current sync status info */
export function getSyncStatus() {
  return {
    status: currentStatus,
    lastSyncAt,
    lastError,
    isConfigured: isSupabaseConfigured(),
  }
}

// ─── Public API ───────────────────────────────────────────────────────

/**
 * Queue a background sync of the multi-account state to Supabase.
 * Called by dataLayer.saveMultiAccountState after the localStorage write.
 * Debounced — rapid successive calls only trigger one sync.
 */
export function queueSync(state: MultiAccountStateV4): void {
  if (!isSupabaseConfigured()) return

  pendingState = state

  // Clear existing timer and set a new one
  if (syncTimer) clearTimeout(syncTimer)
  syncTimer = setTimeout(() => {
    executeSyncIfReady()
  }, SYNC_DEBOUNCE_MS)
}

/**
 * Force an immediate sync (e.g., before page unload).
 * Returns a promise that resolves when sync completes.
 */
export async function flushSync(): Promise<void> {
  if (!isSupabaseConfigured() || !pendingState) return
  if (syncTimer) clearTimeout(syncTimer)
  await executeSyncIfReady()
}

// ─── Core Sync Logic ──────────────────────────────────────────────────

async function executeSyncIfReady(): Promise<void> {
  if (!pendingState || isSyncing) return
  if (!isOnline()) {
    setStatus('offline')
    debug('[sync] Offline — skipping sync, will retry on next write')
    return
  }

  const stateToSync = pendingState
  pendingState = null
  isSyncing = true
  setStatus('syncing')

  try {
    const client = await getSupabaseClient()
    if (!client) {
      setStatus('idle')
      debug('[sync] Supabase client not available — skipping sync')
      return
    }
    await syncMultiAccountState(client, stateToSync)
    setStatus('success')
    debug('[sync] ✓ Background sync completed')
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    setStatus('error', msg)
    logError('[sync] Background sync failed (non-blocking):', msg)
    // Don't re-queue — next write will trigger a new sync
  } finally {
    isSyncing = false
  }
}

// ─── D2: Multi-Account State Decomposition ────────────────────────────
// The localStorage blob is one big JSON object. We decompose it into
// normalized Supabase tables: accounts, classrooms, relationships, etc.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function syncMultiAccountState(
  client: any,
  state: MultiAccountStateV4,
): Promise<void> {
  const { accounts, learnerData } = state
  const { accountsById, relationships, classrooms } = accounts

  // Run all upserts in parallel for speed
  await Promise.all([
    syncAccounts(client, accountsById),
    syncClassrooms(client, classrooms),
    syncRelationships(client, relationships),
    syncClassroomMembers(client, classrooms, relationships),
    syncLearnerStates(client, learnerData),
  ])
}

// ── Accounts ──────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function syncAccounts(
  client: any,
  accountsById: Record<string, Account>,
): Promise<void> {
  const rows: DbAccount[] = Object.values(accountsById).map((acc) => ({
    id: acc.id,
    role: acc.role,
    display_name: acc.displayName,
    username: ('username' in acc ? acc.username : null) ?? null,
    grade: ('grade' in acc ? acc.grade : null) ?? null,
    birth_year: ('birthYear' in acc ? acc.birthYear : null) ?? null,
    school_name: ('schoolName' in acc ? acc.schoolName : null) ?? null,
    phone_number: ('phoneNumber' in acc ? acc.phoneNumber : null) ?? null,
    is_demo: acc.isDemo ?? false,
    is_plus_user: ('isPlusUser' in acc ? acc.isPlusUser : false) ?? false,
    link_code: ('linkCode' in acc ? acc.linkCode : null) ?? null,
    has_completed_onboarding: acc.hasCompletedOnboarding ?? true,
    onboarding_steps: (acc.onboardingSteps as Record<string, unknown>) ?? null,
    created_at: new Date(acc.createdAt).toISOString(),
    last_active: new Date(acc.lastActive).toISOString(),
    updated_at: new Date().toISOString(),
  }))

  if (rows.length === 0) return

  const { error } = await client.from('accounts').upsert(rows, {
    onConflict: 'id',
    ignoreDuplicates: false,
  })

  if (error) throw new Error(`accounts upsert failed: ${error.message}`)
  debug(`[sync] accounts: ${rows.length} upserted`)
}

// ── Classrooms ────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function syncClassrooms(
  client: any,
  classrooms: Record<string, Classroom>,
): Promise<void> {
  const rows: DbClassroom[] = Object.values(classrooms).map((c) => ({
    id: c.id,
    name: c.name,
    group_level: c.groupLevel,
    teacher_id: c.teacherId,
    join_code: c.joinCode,
    created_at: new Date(c.createdAt).toISOString(),
    updated_at: new Date().toISOString(),
  }))

  if (rows.length === 0) return

  const { error } = await client.from('classrooms').upsert(rows, {
    onConflict: 'id',
    ignoreDuplicates: false,
  })

  if (error) throw new Error(`classrooms upsert failed: ${error.message}`)
  debug(`[sync] classrooms: ${rows.length} upserted`)
}

// ── Relationships ─────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function syncRelationships(
  client: any,
  graph: RelationshipGraph,
): Promise<void> {
  const rows: Omit<DbRelationship, 'id' | 'created_at'>[] = []

  // Parent → Learner relationships
  for (const [parentId, learnerIds] of Object.entries(
    graph.parentToLearners ?? {},
  )) {
    for (const childId of learnerIds) {
      rows.push({
        parent_id: parentId,
        child_id: childId,
        relationship_type: 'parent_child',
      })
    }
  }

  // Teacher → Learner relationships
  for (const [teacherId, learnerIds] of Object.entries(
    graph.teacherToLearners ?? {},
  )) {
    for (const learnerId of learnerIds) {
      rows.push({
        parent_id: teacherId,
        child_id: learnerId,
        relationship_type: 'teacher_student',
      })
    }
  }

  if (rows.length === 0) return

  // Delete existing and re-insert (relationships don't have stable IDs)
  const { error: deleteError } = await client
    .from('relationships')
    .delete()
    .neq('id', '00000000-0000-0000-0000-000000000000') // Delete all rows

  if (deleteError)
    warn('[sync] relationships delete failed:', deleteError.message)

  const { error } = await client.from('relationships').insert(rows)

  if (error) throw new Error(`relationships insert failed: ${error.message}`)
  debug(`[sync] relationships: ${rows.length} synced`)
}

// ── Classroom Members ─────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function syncClassroomMembers(
  client: any,
  classrooms: Record<string, Classroom>,
  relationships: RelationshipGraph,
): Promise<void> {
  const rows: DbClassroomMember[] = []

  for (const classroom of Object.values(classrooms)) {
    for (const learnerId of classroom.learnerIds ?? []) {
      rows.push({
        classroom_id: classroom.id,
        learner_id: learnerId,
        joined_at: new Date().toISOString(),
      })
    }
  }

  // Also check learnerToClassrooms for any we missed
  for (const [learnerId, classroomIds] of Object.entries(
    relationships.learnerToClassrooms ?? {},
  )) {
    for (const classroomId of classroomIds) {
      if (
        !rows.some(
          (r) => r.classroom_id === classroomId && r.learner_id === learnerId,
        )
      ) {
        rows.push({
          classroom_id: classroomId,
          learner_id: learnerId,
          joined_at: new Date().toISOString(),
        })
      }
    }
  }

  // Delete all and re-insert (membership is derived from state)
  const { error: deleteError } = await client
    .from('classroom_members')
    .delete()
    .neq('classroom_id', '00000000-0000-0000-0000-000000000000')

  if (deleteError)
    warn('[sync] classroom_members delete failed:', deleteError.message)

  if (rows.length === 0) return

  const { error } = await client.from('classroom_members').upsert(rows, {
    onConflict: 'classroom_id,learner_id',
    ignoreDuplicates: false,
  })

  if (error)
    throw new Error(`classroom_members upsert failed: ${error.message}`)
  debug(`[sync] classroom_members: ${rows.length} synced`)
}

// ── Learner States (mastery, progress, etc.) ──────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function syncLearnerStates(
  client: any,
  learnerData: Record<string, unknown>,
): Promise<void> {
  const rows: DbLearnerState[] = Object.entries(learnerData).map(
    ([learnerId, state]) => ({
      learner_id: learnerId,
      state: state as Record<string, unknown>,
      version: 1,
      updated_at: new Date().toISOString(),
    }),
  )

  if (rows.length === 0) return

  const { error } = await client.from('learner_states').upsert(rows, {
    onConflict: 'learner_id',
    ignoreDuplicates: false,
  })

  if (error) throw new Error(`learner_states upsert failed: ${error.message}`)
  debug(`[sync] learner_states: ${rows.length} upserted`)
}

// ─── Page Unload Handler ──────────────────────────────────────────────
// Attempt to flush pending sync before the user leaves

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (pendingState && isSupabaseConfigured() && isOnline()) {
      debug('[sync] Page unloading with pending sync — will sync on next load')
    }
  })

  // Sync when coming back online
  window.addEventListener('online', () => {
    if (pendingState) {
      debug('[sync] Back online — triggering pending sync')
      executeSyncIfReady()
    }
  })
}
